import { useState, useEffect, useMemo, useRef } from 'react';

const API = import.meta.env.VITE_API_URL || '/api';

interface Register {
  id: string;
  name: string;
  type: string;
  currentBalance: string;
  openingBalance?: string;
  bankName?: string;
  accountNumber?: string;
  accountType?: string;
  alias?: string;
  posEnabled?: boolean;
  isPos?: boolean;
  createdAt: string;
  active?: boolean;
  balance?: string;
}

interface Shift {
  id: string;
  registerId: string;
  status: 'open' | 'closed' | 'suspended';
  openingBalance: string;
  closingBalance?: string;
  expectedBalance?: string;
  variance?: string;
  openedAt: string;
  closedAt?: string;
  openingNote?: string;
  closingNote?: string;
  totalIn?: string;
  totalOut?: string;
  userId?: string;
  closingUserId?: string;
  openedByName?: string;
  openedByEmail?: string;
  closedByName?: string;
}

interface Movement {
  id: string;
  registerId: string;
  type: string;
  direction: 'in' | 'out';
  amount: string;
  reason: string;
  reference?: string;
  balanceAfter?: string;
  createdAt: string;
}

interface Transfer {
  id: string;
  fromRegisterId: string;
  toRegisterId: string;
  amount: string;
  reason: string;
  createdAt: string;
}

interface CashflowItem {
  registerId: string;
  name: string;
  type: string;
  currentBalance: string;
  pendingSettlements: number;
  pendingGross: string;
  pendingCommission: string;
  pendingNet: string;
  dueNow: string;
  projectedBalance: string;
  availableNow: string;
}

interface TimelineFlow {
  type?: string;
  registerName?: string;
  poNumber?: string;
  net?: string;
  amount?: string;
}

interface TimelineItem {
  date: string;
  totalInflow?: string;
  totalOutflow?: string;
  netFlow?: string;
  inflows?: TimelineFlow[];
  outflows?: TimelineFlow[];
}

interface CardTerminal {
  id: string;
  name: string;
  provider: string;
  commissionDebit: string;
  commissionCredit: string;
  accreditationDaysDebit: number | string;
  accreditationDaysCredit: number | string;
  registerId: string;
  acceptedCards: string;
  active: boolean;
}

interface CardSettlement {
  id: string;
  saleId?: string;
  status: 'pending' | 'accredited';
  cardType: 'credit' | 'debit';
  saleDate: string;
  accreditationDate: string;
  accreditedAt?: string;
  grossAmount: string;
  commissionAmount: string;
  netAmount: string;
}

interface BalanceSummary {
  total: string;
  byType: Record<string, number>;
  registers: Register[];
  openShifts: number;
  openDrawerBalance?: string;
  cashDrawerBalance?: string;
}

const MOVEMENT_TYPES = [
  { value: 'float_entry', label: 'Fondo / Reposición', direction: 'in' as const },
  { value: 'tender_removal', label: 'Retiro de dinero', direction: 'out' as const },
  { value: 'safe_drop', label: 'Depósito a caja fuerte', direction: 'out' as const },
  { value: 'bank_drop', label: 'Depósito a banco', direction: 'out' as const },
  { value: 'supplier_payment', label: 'Pago a proveedor', direction: 'out' as const },
  { value: 'employee_advance', label: 'Adelanto a empleado', direction: 'out' as const },
  { value: 'petty_cash', label: 'Gasto menor', direction: 'out' as const },
  { value: 'adjustment', label: 'Ajuste', direction: 'in' as const },
];

const REGISTER_TYPES: Record<string, string> = {
  cash_drawer: 'Caja Física',
  safe: 'Caja Fuerte',
  bank_account: 'Cuenta Bancaria',
  petty_cash: 'Caja Chica',
};

const REGISTER_ICONS: Record<string, string> = {
  cash_drawer: '💵',
  safe: '🔒',
  bank_account: '🏦',
  petty_cash: '📋',
};

const PROVIDER_PRESETS: Record<string, { name: string; commissionDebit: string; commissionCredit: string; accredDebit: string; accredCredit: string }> = {
  mercado_pago: { name: 'Mercado Pago Point', commissionDebit: '4.39', commissionCredit: '6.29', accredDebit: '1', accredCredit: '3' },
  uala: { name: 'Ualá POS', commissionDebit: '2.5', commissionCredit: '3.0', accredDebit: '1', accredCredit: '1' },
  modo: { name: 'MODO', commissionDebit: '1.5', commissionCredit: '2.0', accredDebit: '1', accredCredit: '2' },
  bank: { name: 'Posnet Bancario', commissionDebit: '1.8', commissionCredit: '1.8', accredDebit: '8', accredCredit: '10' },
  first_data: { name: 'LaPos / Getnet', commissionDebit: '2.5', commissionCredit: '3.5', accredDebit: '1', accredCredit: '3' },
  other: { name: '', commissionDebit: '0', commissionCredit: '0', accredDebit: '1', accredCredit: '1' },
};

function formatMoney(value: number | string | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (!Number.isFinite(num)) return '$0.00';
  return `$${num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDateTime(value: string | undefined): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('es-AR');
}

function formatDate(value: string | undefined): string {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function classNames(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
    </div>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-red-50 text-red-700 dark:text-red-300 p-4 rounded-lg mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <span className="text-sm font-medium">⚠️ {message}</span>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors min-h-[44px]"
      >
        Reintentar
      </button>
    </div>
  );
}

function EmptyState({ message, action }: { message: string; action?: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
      <p className="text-4xl mb-3">📭</p>
      <p className="text-gray-600 dark:text-gray-300 font-medium text-sm">{message}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

function Modal({
  title,
  children,
  onClose,
  onSave,
  saveLabel = 'Guardar',
  saveDisabled = false,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  onSave: () => void;
  saveLabel?: string;
  saveDisabled?: boolean;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-2 rounded-lg min-h-[44px] min-w-[44px]"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>
        <div className="p-4 space-y-4">{children}</div>
        <div className="flex gap-3 p-4 border-t">
          <button
            onClick={onSave}
            disabled={saveDisabled}
            className={classNames(
              'flex-1 px-4 py-3 rounded-lg font-semibold text-white min-h-[48px] transition-colors',
              saveDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
            )}
          >
            {saveLabel}
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 rounded-lg font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 min-h-[48px] transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

export function CashManagement() {
  const me = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } })();
  const isCashier = me?.role === 'cashier';
  const [tab, setTab] = useState<'overview' | 'registers' | 'shifts' | 'movements' | 'transfers' | 'cashflow' | 'terminals' | 'credit'>(isCashier ? 'shifts' : 'overview');

  const [registers, setRegisters] = useState<Register[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [balance, setBalance] = useState<BalanceSummary | null>(null);
  const [cashflow, setCashflow] = useState<CashflowItem[]>([]);
  const [cashflowTimeline, setCashflowTimeline] = useState<TimelineItem[]>([]);
  const [settlements, setSettlements] = useState<CardSettlement[]>([]);
  const [cardTerminals, setCardTerminals] = useState<CardTerminal[]>([]);
  // Cuenta corriente de clientes
  const [creditSummary, setCreditSummary] = useState<{ id: string; name: string; balance: number; creditLimit: number; creditEnabled: boolean; pendingDebits: number; nextPaymentDate: string | null }[]>([]);
  const [payCustomer, setPayCustomer] = useState<{ id: string; name: string; balance: number } | null>(null);
  const [payAmount, setPayAmount] = useState('');
  const [payRegisterId, setPayRegisterId] = useState('');
  const [payNote, setPayNote] = useState('');
  const [paying, setPaying] = useState(false);
  // Histórico de cuenta corriente (modal)
  const [histCustomer, setHistCustomer] = useState<{ id: string; name: string; balance: number; creditLimit: number; creditDays: number } | null>(null);
  const [histMovements, setHistMovements] = useState<{ id: string; type: string; amount: number; saleId: string | null; paymentDate: string | null; status: string; note: string | null; createdAt: string }[]>([]);
  const [histLoading, setHistLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [modal, setModal] = useState<string | null>(null);

  // Forms
  const [regForm, setRegForm] = useState({
    name: '',
    type: 'cash_drawer',
    openingBalance: '',
    bankName: '',
    accountNumber: '',
    accountType: '',
    alias: '',
    posEnabled: false,
    isPos: false,
  });
  const [editRegister, setEditRegister] = useState<Register | null>(null);

  const [shiftForm, setShiftForm] = useState({ registerId: '', openingBalance: '', note: '' });
  const [closeForm, setCloseForm] = useState({ countedBalance: '', note: '' });
  const [closeShiftId, setCloseShiftId] = useState('');

  const [moveForm, setMoveForm] = useState({ registerId: '', type: 'float_entry', amount: '', reason: '', reference: '' });

  // Aporte/retiro de efectivo (cambio) por owner/manager sobre una caja abierta
  const [cashOpForm, setCashOpForm] = useState<{ registerId: string; direction: 'in' | 'out'; amount: string; reason: string } | null>(null);

  const [transferForm, setTransferForm] = useState({ fromRegisterId: '', toRegisterId: '', amount: '', reason: '' });

  const [termForm, setTermForm] = useState({
    name: '',
    provider: 'mercado_pago',
    commissionDebit: '4.39',
    commissionCredit: '6.29',
    accreditationDaysDebit: '1',
    accreditationDaysCredit: '3',
    registerId: '',
    acceptedCards: 'both',
    active: true,
  });
  const [editTerminal, setEditTerminal] = useState<CardTerminal | null>(null);

  const movementReasonRef = useRef<HTMLInputElement>(null);

  const token = localStorage.getItem('accessToken');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
  };

  const openShifts = useMemo(() => shifts.filter(s => s.status === 'open'), [shifts]);
  const firstOpenShift = openShifts[0];

  function showSuccess(message: string) {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  }

  async function loadCore() {
    setError(null);
    try {
      if (isCashier) {
        const [regRes, shiftRes] = await Promise.all([
          fetch(`${API}/cash/registers`, { headers }),
          fetch(`${API}/cash/shifts`, { headers }),
        ]);
        if (!regRes.ok) throw new Error('Error al cargar las cuentas');
        if (!shiftRes.ok) throw new Error('Error al cargar las cajas');
        const regData = await regRes.json();
        const shiftData = await shiftRes.json();
        const regs = regData.data || [];
        const shifts = shiftData.data || [];
        setRegisters(regs);
        setShifts(shifts);

        // El cajero no llama a /api/cash/balance; construimos el balance local
        // a partir de registers + shifts para que el hero muestre el estado real.
        const openShiftsLocal = shifts.filter((s: any) => s.status === 'open');
        const openDrawerIds = openShiftsLocal.map((s: any) => s.registerId);
        const cashDrawersLocal = regs.filter((r: any) => r.type === 'cash_drawer');
        const openDrawerBalance = cashDrawersLocal
          .filter((r: any) => openDrawerIds.includes(r.id))
          .reduce((sum: number, r: any) => sum + parseFloat(r.currentBalance || '0'), 0);
        const cashDrawerBalance = cashDrawersLocal
          .reduce((sum: number, r: any) => sum + parseFloat(r.currentBalance || '0'), 0);
        const byTypeLocal: Record<string, number> = {};
        for (const r of regs) {
          if (!byTypeLocal[r.type]) byTypeLocal[r.type] = 0;
          byTypeLocal[r.type] += parseFloat(r.currentBalance || '0');
        }
        setBalance({
          total: Object.values(byTypeLocal).reduce((a, b) => a + b, 0).toFixed(2),
          byType: byTypeLocal,
          registers: regs.map((r: any) => ({ id: r.id, name: r.name, type: r.type, balance: r.currentBalance, currentBalance: r.currentBalance })),
          openShifts: openShiftsLocal.length,
          openDrawerBalance: openDrawerBalance.toFixed(2),
          cashDrawerBalance: cashDrawerBalance.toFixed(2),
        });
        return;
      }
      const [regRes, balRes, shiftRes] = await Promise.all([
        fetch(`${API}/cash/registers`, { headers }),
        fetch(`${API}/cash/balance`, { headers }),
        fetch(`${API}/cash/shifts`, { headers }),
      ]);

      if (!regRes.ok) throw new Error('Error al cargar las cuentas');
      if (!balRes.ok) throw new Error('Error al cargar el balance');
      if (!shiftRes.ok) throw new Error('Error al cargar las cajas');

      const regData = await regRes.json();
      const balData = await balRes.json();
      const shiftData = await shiftRes.json();

      setRegisters(regData.data || []);
      setBalance(balData);
      setShifts(shiftData.data || []);
    } catch (e: any) {
      setError(e.message || 'Error al cargar los datos');
    }
  }

  async function loadTabData() {
    setError(null);
    setLoading(true);
    try {
      if (tab === 'movements') {
        const res = await fetch(`${API}/cash/movements`, { headers });
        if (!res.ok) throw new Error('Error al cargar movimientos');
        const data = await res.json();
        setMovements(data.data || []);
      }
      if (tab === 'transfers') {
        const res = await fetch(`${API}/cash/transfers`, { headers });
        if (!res.ok) throw new Error('Error al cargar transferencias');
        const data = await res.json();
        setTransfers(data.data || []);
      }
      if (tab === 'cashflow') {
        const [cfRes, tlRes] = await Promise.all([
          fetch(`${API}/cash/cashflow`, { headers }),
          fetch(`${API}/cash/cashflow/timeline`, { headers }),
        ]);
        if (!cfRes.ok) throw new Error('Error al cargar cashflow');
        if (!tlRes.ok) throw new Error('Error al cargar timeline');
        const cfData = await cfRes.json();
        const tlData = await tlRes.json();
        setCashflow(cfData.data || []);
        setCashflowTimeline(tlData.data || []);
      }
      if (tab === 'terminals') {
        const [setRes, termRes] = await Promise.all([
          fetch(`${API}/cash/card-settlements?status=all`, { headers }),
          fetch(`${API}/cash/card-terminals`, { headers }),
        ]);
        if (!setRes.ok) throw new Error('Error al cargar acreditaciones');
        if (!termRes.ok) throw new Error('Error al cargar terminales');
        const setData = await setRes.json();
        const termData = await termRes.json();
        setSettlements(setData.data || []);
        setCardTerminals(termData.data || []);
      }
      if (tab === 'credit') {
        const res = await fetch(`${API}/customers/credit-summary`, { headers });
        if (!res.ok) throw new Error('Error al cargar cuentas corrientes');
        const data = await res.json();
        setCreditSummary(data.data || []);
      }
    } catch (e: any) {
      setError(e.message || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCore();
  }, []);

  // Registrar pago de cuenta corriente (entrada a caja + credito al cliente)
  async function recordPayment() {
    if (!payCustomer || !payAmount || Number(payAmount) <= 0) return;
    setPaying(true);
    setError(null);
    try {
      const res = await fetch(`${API}/customers/${payCustomer.id}/payments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          amount: Number(payAmount),
          registerId: payRegisterId || undefined,
          note: payNote || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al registrar el pago');
      }
      showSuccess(`Pago de $${Number(payAmount).toFixed(2)} registrado a ${payCustomer.name}`);
      setPayCustomer(null);
      setPayAmount('');
      setPayNote('');
      setPayRegisterId('');
      await loadTabData();
      await loadCore();
    } catch (e: any) {
      setError(e.message || 'Error al registrar el pago');
    } finally {
      setPaying(false);
    }
  }

  // Histórico de cuenta corriente (GET /api/customers/:id/account)
  async function loadHistorico(c: { id: string; name: string }) {
    setHistCustomer({ ...c, balance: 0, creditLimit: 0, creditDays: 0 });
    setHistMovements([]);
    setHistLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/customers/${c.id}/account`, { headers });
      if (!res.ok) throw new Error(`Error ${res.status} al cargar el histórico`);
      const json = (await res.json()) as { data: {
        customer: { id: string; name: string; creditEnabled: boolean; creditLimit: string; creditDays: number };
        balance: number;
        movements: { id: string; type: string; amount: number; saleId: string | null; paymentDate: string | null; status: string; note: string | null; createdAt: string }[];
      } };
      setHistCustomer({
        id: json.data.customer.id,
        name: json.data.customer.name,
        balance: json.data.balance,
        creditLimit: Number(json.data.customer.creditLimit || 0),
        creditDays: json.data.customer.creditDays ?? 0,
      });
      setHistMovements(json.data.movements || []);
    } catch (e: any) {
      setError(e.message || 'Error al cargar el histórico');
      setHistCustomer(null);
    } finally {
      setHistLoading(false);
    }
  }

  useEffect(() => {
    loadTabData();
  }, [tab]);

  async function createRegister() {
    if (!regForm.name.trim()) return;
    try {
      const res = await fetch(`${API}/cash/registers`, {
        method: 'POST',
        headers,
        body: JSON.stringify(regForm),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al crear cuenta');
      }
      setModal(null);
      setRegForm({ name: '', type: 'cash_drawer', openingBalance: '', bankName: '', accountNumber: '', accountType: '', alias: '', posEnabled: false, isPos: false });
      showSuccess('Cuenta creada');
      await loadCore();
      if (tab !== 'overview') await loadTabData();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function saveEditRegister() {
    if (!editRegister) return;
    try {
      const res = await fetch(`${API}/cash/registers/${editRegister.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          name: editRegister.name,
          bankName: editRegister.bankName || '',
          accountNumber: editRegister.accountNumber || '',
          accountType: editRegister.accountType || '',
          alias: editRegister.alias || '',
          posEnabled: editRegister.posEnabled || false,
          isPos: editRegister.isPos || false,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al actualizar cuenta');
      }
      setModal(null);
      setEditRegister(null);
      showSuccess('Cuenta actualizada');
      await loadCore();
      if (tab !== 'overview') await loadTabData();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function openShift() {
    if (!shiftForm.registerId || !shiftForm.openingBalance) return;
    try {
      const res = await fetch(`${API}/cash/shifts/open`, {
        method: 'POST',
        headers,
        body: JSON.stringify(shiftForm),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al abrir la caja');
      }
      setModal(null);
      setShiftForm({ registerId: '', openingBalance: '', note: '' });
      showSuccess('Caja abierta');
      await loadCore();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function closeShift() {
    if (!closeShiftId || !closeForm.countedBalance) return;
    try {
      const res = await fetch(`${API}/cash/shifts/${closeShiftId}/close`, {
        method: 'POST',
        headers,
        body: JSON.stringify(closeForm),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al cerrar la caja');
      }
      setModal(null);
      setCloseForm({ countedBalance: '', note: '' });
      setCloseShiftId('');
      showSuccess('Caja cerrada');
      await loadCore();
    } catch (e: any) {
      setError(e.message);
    }
  }

  // Aporte de efectivo (owner/manager: agregar cambio) o retiro → movimiento
  async function submitCashOp() {
    if (!cashOpForm) return;
    const { registerId, direction, amount, reason } = cashOpForm;
    if (!registerId || !amount || !reason.trim()) return;
    try {
      const res = await fetch(`${API}/cash/movements`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          registerId,
          direction,
          type: direction === 'in' ? 'float_entry' : 'tender_removal',
          amount,
          reason: reason.trim(),
          referenceType: 'manual',
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al registrar el movimiento');
      }
      setCashOpForm(null);
      showSuccess(direction === 'in' ? 'Efectivo aportado a la caja' : 'Efectivo retirado de la caja');
      await loadCore();
      if (tab !== 'overview') await loadTabData();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function createMovement() {
    if (!moveForm.registerId || !moveForm.amount || !moveForm.reason) return;
    const moveType = MOVEMENT_TYPES.find(t => t.value === moveForm.type);
    try {
      const res = await fetch(`${API}/cash/movements`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...moveForm, direction: moveType?.direction }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al registrar movimiento');
      }
      setMoveForm({ registerId: '', type: 'float_entry', amount: '', reason: '', reference: '' });
      showSuccess('Movimiento registrado');
      await loadCore();
      await loadTabData();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function createTransfer() {
    if (!transferForm.fromRegisterId || !transferForm.toRegisterId || !transferForm.amount || !transferForm.reason) return;
    try {
      const res = await fetch(`${API}/cash/transfers`, {
        method: 'POST',
        headers,
        body: JSON.stringify(transferForm),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al transferir');
      }
      setModal(null);
      setTransferForm({ fromRegisterId: '', toRegisterId: '', amount: '', reason: '' });
      showSuccess('Transferencia realizada');
      await loadCore();
      await loadTabData();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function createTerminal() {
    if (!termForm.name || !termForm.provider || !termForm.registerId) return;
    try {
      const res = await fetch(`${API}/cash/card-terminals`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...termForm,
          accreditationDaysDebit: parseInt(termForm.accreditationDaysDebit),
          accreditationDaysCredit: parseInt(termForm.accreditationDaysCredit),
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al crear terminal');
      }
      setModal(null);
      setTermForm({
        name: '', provider: 'mercado_pago', commissionDebit: '4.39', commissionCredit: '6.29',
        accreditationDaysDebit: '1', accreditationDaysCredit: '3', registerId: '', acceptedCards: 'both', active: true,
      });
      showSuccess('Terminal creado');
      await loadTabData();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function saveEditTerminal() {
    if (!editTerminal) return;
    try {
      const res = await fetch(`${API}/cash/card-terminals/${editTerminal.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          name: editTerminal.name,
          provider: editTerminal.provider,
          commissionDebit: editTerminal.commissionDebit,
          commissionCredit: editTerminal.commissionCredit,
          accreditationDaysDebit: parseInt(String(editTerminal.accreditationDaysDebit)),
          accreditationDaysCredit: parseInt(String(editTerminal.accreditationDaysCredit)),
          registerId: editTerminal.registerId,
          acceptedCards: editTerminal.acceptedCards,
          active: editTerminal.active,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al actualizar terminal');
      }
      setModal(null);
      setEditTerminal(null);
      showSuccess('Terminal actualizado');
      await loadTabData();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function accreditSettlements() {
    try {
      const res = await fetch(`${API}/cash/card-settlements/accrete`, { method: 'POST', headers });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al acreditar');
      }
      const data = await res.json();
      showSuccess(data.message || 'Acreditaciones procesadas');
      await loadTabData();
      await loadCore();
    } catch (e: any) {
      setError(e.message);
    }
  }

  function applyProviderPreset(provider: string) {
    const preset = PROVIDER_PRESETS[provider];
    if (preset) {
      setTermForm(f => ({
        ...f,
        provider,
        name: preset.name || f.name,
        commissionDebit: preset.commissionDebit,
        commissionCredit: preset.commissionCredit,
        accreditationDaysDebit: preset.accredDebit,
        accreditationDaysCredit: preset.accredCredit,
      }));
    }
  }

  function openEditModal(r: Register) {
    setEditRegister({ ...r });
    setModal('editRegister');
  }

  function openEditTerminalModal(t: CardTerminal) {
    setEditTerminal({ ...t });
    setModal('editTerminal');
  }

  function openCloseModal(shiftId?: string) {
    const target = shiftId || firstOpenShift?.id;
    if (!target) return;
    setCloseShiftId(target);
    setCloseForm({ countedBalance: '', note: '' });
    setModal('closeShift');
  }

  const tabs = isCashier
    ? [{ id: 'shifts' as const, label: '🕐 Cajas' }]
    : [
    { id: 'overview' as const, label: '📊 Resumen' },
    { id: 'registers' as const, label: '🏦 Cuentas' },
    { id: 'shifts' as const, label: '🕐 Cajas' },
    { id: 'movements' as const, label: '💰 Movimientos' },
    { id: 'transfers' as const, label: '🔄 Transferencias' },
    { id: 'cashflow' as const, label: '📈 Cashflow' },
    { id: 'credit' as const, label: '📒 Cuenta Corriente' },
    { id: 'terminals' as const, label: '💳 Tarjetas' },
  ];

  const canOpenShift = registers.length > 0;
  const heroIsOpen = (balance?.openShifts || 0) > 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">💵 Gestión de Caja</h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1 text-sm sm:text-base">Controlá cajas, movimientos, cuentas y acreditaciones.</p>
      </div>

      {/* Alerts */}
      {success && (
        <div className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 p-3 rounded-lg mb-4 text-sm font-medium">
          ✓ {success}
        </div>
      )}
      {error && <ErrorBanner message={error} onRetry={() => { loadCore(); loadTabData(); }} />}

      {/* Hero: register status */}
      <div
        className={classNames(
          'rounded-2xl p-5 sm:p-6 mb-6 text-white shadow-lg',
          heroIsOpen ? 'bg-green-600' : 'bg-gray-700'
        )}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-sm sm:text-base font-medium opacity-90">
              {heroIsOpen ? 'Caja Abierta — Efectivo real de la caja' : 'Caja Cerrada'}
            </p>
            <p className="text-3xl sm:text-4xl font-bold mt-1">
              {formatMoney(heroIsOpen ? balance?.openDrawerBalance : balance?.cashDrawerBalance)}
            </p>
            <p className="text-sm opacity-80 mt-1">
              {heroIsOpen
                ? `${balance?.openShifts || 0} caja${(balance?.openShifts || 0) === 1 ? '' : 's'} física${(balance?.openShifts || 0) === 1 ? '' : 's'} abierta${(balance?.openShifts || 0) === 1 ? '' : 's'} — solo efectivo, sin cuentas bancarias`
                : `${balance?.cashDrawerBalance != null ? 'Solo efectivo de cajas físicas' : 'Cargando...'}`}
            </p>
          </div>
          <button
            onClick={() => {
              if (heroIsOpen) {
                openCloseModal();
              } else {
                if (registers.length > 0 && !shiftForm.registerId) {
                  setShiftForm(f => ({ ...f, registerId: registers[0].id }));
                }
                setModal('openShift');
              }
            }}
            disabled={!heroIsOpen && !canOpenShift}
            className={classNames(
              'w-full sm:w-auto px-6 py-4 rounded-xl font-bold text-lg shadow transition-colors min-h-[56px] sm:min-h-[64px]',
              !heroIsOpen && !canOpenShift
                ? 'bg-gray-400 cursor-not-allowed'
                : heroIsOpen
                  ? 'bg-red-500 hover:bg-red-600'
                  : 'bg-blue-500 hover:bg-blue-600'
            )}
          >
            {heroIsOpen ? '🔒 Cerrar Caja' : '🔓 Abrir Caja'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={classNames(
                'px-4 py-3 text-sm font-semibold whitespace-nowrap min-h-[44px] transition-colors border-b-2',
                tab === t.id
                  ? 'text-blue-600 dark:text-blue-400 border-blue-600'
                  : 'text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-200'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ===== OVERVIEW ===== */}
      {tab === 'overview' && (
        <section className="space-y-4">
          {!balance ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(balance.byType || {}).map(([type, amount]) => (
                  <div key={type} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4" style={{ borderLeftColor: heroIsOpen ? '#16a34a' : '#374151' }}>
                    <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 uppercase tracking-wide font-semibold">{REGISTER_ICONS[type]} {REGISTER_TYPES[type] || type}</p>
                    <p className="text-xl font-bold mt-1">{formatMoney(amount)}</p>
                  </div>
                ))}
              </div>

              <h2 className="text-lg font-bold mt-6 mb-3">Cuentas activas</h2>
              {balance.registers.length === 0 ? (
                <EmptyState message="No hay cuentas configuradas." />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {balance.registers.map(r => {
                    const hasOpenShift = openShifts.some(s => s.registerId === r.id);
                    return (
                      <div key={r.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-gray-800 dark:text-gray-100">{REGISTER_ICONS[r.type]} {r.name} {r.isPos && <span className="text-[10px] bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-full px-2 py-0.5 ml-1">🛒 POS</span>}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{REGISTER_TYPES[r.type] || r.type}</p>
                          </div>
                          <span className={classNames('text-xs px-2 py-1 rounded-full font-medium text-white', hasOpenShift ? 'bg-green-500' : 'bg-gray-400')}>
                            {hasOpenShift ? 'Abierta' : 'Cerrada'}
                          </span>
                        </div>
                        <p className={classNames('text-2xl font-bold mt-3', parseFloat(r.balance || '0') >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600')}>
                          {formatMoney(r.balance || '0')}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* ===== REGISTERS ===== */}
      {tab === 'registers' && (
        <section>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-lg font-bold">Cuentas</h2>
            <button
              onClick={() => setModal('register')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium min-h-[44px] transition-colors"
            >
              + Nueva Cuenta
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : registers.length === 0 ? (
            <EmptyState
              message="No hay cuentas configuradas."
              action={
                <button
                  onClick={() => setModal('register')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium min-h-[44px]"
                >
                  Crear primera cuenta
                </button>
              }
            />
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="block sm:hidden">
                {registers.map(r => (
                  <div key={r.id} className="p-4 border-b last:border-b-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-gray-800 dark:text-gray-100">{REGISTER_ICONS[r.type]} {r.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{REGISTER_TYPES[r.type] || r.type}</p>
                        {r.bankName && <p className="text-xs text-gray-400 dark:text-gray-400">{r.bankName} — {r.accountNumber}</p>}
                        {r.alias && <p className="text-xs text-purple-600 font-medium">Alias: {r.alias}</p>}
                      </div>
                      <button
                        onClick={() => openEditModal(r)}
                        className="text-blue-600 dark:text-blue-400 text-sm font-medium px-2 py-1 min-h-[44px]"
                      >
                        Editar
                      </button>
                    </div>
                    <p className={classNames('text-xl font-bold mt-2', parseFloat(r.currentBalance) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600')}>
                      {formatMoney(r.currentBalance)}
                    </p>
                    {r.posEnabled && <span className="inline-block mt-2 text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-full">POS ✓</span>}
                  </div>
                ))}
              </div>
              <table className="hidden sm:table w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Cuenta</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Tipo</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600 dark:text-gray-300">Saldo</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 dark:text-gray-300">POS</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600 dark:text-gray-300">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {registers.map(r => (
                    <tr key={r.id} className="border-t">
                      <td className="px-4 py-3">
                        <p className="font-medium">{REGISTER_ICONS[r.type]} {r.name}</p>
                        {r.alias && <p className="text-xs text-purple-600">Alias: {r.alias}</p>}
                        {r.bankName && <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{r.bankName} — {r.accountNumber}</p>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{REGISTER_TYPES[r.type] || r.type}</td>
                      <td className={classNames('px-4 py-3 text-right font-bold', parseFloat(r.currentBalance) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600')}>
                        {formatMoney(r.currentBalance)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {r.posEnabled ? <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-full">Sí</span> : <span className="text-xs text-gray-400 dark:text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => openEditModal(r)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 text-sm font-medium px-2 py-1 min-h-[44px]"
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ===== SHIFTS ===== */}
      {tab === 'shifts' && (
        <section>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-lg font-bold">Cajas</h2>
            {openShifts.length === 0 && (
              <button
                onClick={() => {
                  if (registers.length > 0 && !shiftForm.registerId) {
                    setShiftForm(f => ({ ...f, registerId: registers[0].id }));
                  }
                  setModal('openShift');
                }}
                disabled={!canOpenShift}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg font-medium min-h-[44px] transition-colors"
              >
                + Abrir Caja
              </button>
            )}
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : shifts.length === 0 ? (
            <EmptyState message="No hay cajas registradas." />
          ) : (
            <div className="space-y-3">
              {shifts.map(s => {
                const register = registers.find(r => r.id === s.registerId);
                return (
                  <div key={s.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-800 dark:text-gray-100">Caja #{s.id.slice(0, 8)}</p>
                          <span className={classNames('text-xs px-2 py-0.5 rounded-full font-medium text-white', s.status === 'open' ? 'bg-green-500' : s.status === 'suspended' ? 'bg-amber-500' : 'bg-gray-500')}>
                            {s.status === 'open' ? 'Abierto' : s.status === 'closed' ? 'Cerrado' : 'Suspendido'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
                          {register?.name || 'Cuenta desconocida'} · Apertura: {formatMoney(s.openingBalance)} · {formatDateTime(s.openedAt)}
                        </p>
                        {s.openedByName && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">👤 Abrió: {s.openedByName}</p>
                        )}
                        {s.status === 'open' && register && (
                          <p className="text-sm font-medium text-green-700 dark:text-green-300 mt-1">
                            💵 Efectivo disponible: {formatMoney(register.currentBalance)}
                          </p>
                        )}
                        {s.status === 'closed' && (
                          <>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                              Cierre: {formatMoney(s.closingBalance)} (esperado: {formatMoney(s.expectedBalance)})
                              <span className={classNames('ml-2 font-semibold', parseFloat(s.variance || '0') === 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600')}>
                                Dif: {formatMoney(s.variance)}
                              </span>
                            </p>
                            {s.closedByName && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">👤 Cerró: {s.closedByName} · {formatDateTime(s.closedAt)}</p>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {s.status === 'open' && (
                          <>
                            <button
                              onClick={() => setCashOpForm({ registerId: s.registerId, direction: 'in', amount: '', reason: '' })}
                              className="px-3 py-2 bg-green-100 dark:bg-green-900/40 hover:bg-green-200 text-green-700 dark:text-green-300 rounded-lg text-sm font-medium min-h-[44px] transition-colors"
                              title="Aportar efectivo (ej: cambio)"
                            >
                              💵 Aportar
                            </button>
                            <button
                              onClick={() => setCashOpForm({ registerId: s.registerId, direction: 'out', amount: '', reason: '' })}
                              className="px-3 py-2 bg-amber-100 dark:bg-amber-900/40 hover:bg-amber-200 text-amber-700 dark:text-amber-300 rounded-lg text-sm font-medium min-h-[44px] transition-colors"
                              title="Retirar efectivo de la caja"
                            >
                              🏦 Retirar
                            </button>
                            <button
                              onClick={() => openCloseModal(s.id)}
                              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium min-h-[44px] transition-colors"
                            >
                              Cerrar
                            </button>
                          </>
                        )}
                        {s.status === 'closed' && (
                          <a
                            href={`${API}/cash/shifts/${s.id}/summary`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium min-h-[44px] inline-flex items-center transition-colors"
                          >
                            📋 Z-Report
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ===== MOVEMENTS ===== */}
      {tab === 'movements' && (
        <section>
          <h2 className="text-lg font-bold mb-4">Nuevo Movimiento</h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Cuenta</label>
                <select
                  value={moveForm.registerId}
                  onChange={e => setMoveForm({ ...moveForm, registerId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                >
                  <option value="">Seleccionar...</option>
                  {registers.map(r => (
                    <option key={r.id} value={r.id}>{r.name} ({formatMoney(r.currentBalance)})</option>
                  ))}
                </select>
              </div>
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Tipo</label>
                <select
                  value={moveForm.type}
                  onChange={e => setMoveForm({ ...moveForm, type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                >
                  {MOVEMENT_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.direction === 'in' ? '↑' : '↓'} {t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Monto ($)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={moveForm.amount}
                  onChange={e => setMoveForm({ ...moveForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={createMovement}
                  disabled={!moveForm.registerId || !moveForm.amount || !moveForm.reason}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium min-h-[44px] transition-colors"
                >
                  Registrar
                </button>
              </div>
              <div className="lg:col-span-4">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Concepto</label>
                <input
                  ref={movementReasonRef}
                  type="text"
                  placeholder="Motivo del movimiento"
                  value={moveForm.reason}
                  onChange={e => setMoveForm({ ...moveForm, reason: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
              </div>
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Referencia (opcional)</label>
                <input
                  type="text"
                  placeholder="PO-001, FACT-A-0001"
                  value={moveForm.reference}
                  onChange={e => setMoveForm({ ...moveForm, reference: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
              </div>
            </div>
          </div>

          <h2 className="text-lg font-bold mb-4">Movimientos recientes</h2>
          {loading ? (
            <LoadingSpinner />
          ) : movements.length === 0 ? (
            <EmptyState message="No hay movimientos registrados." />
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="block sm:hidden">
                {movements.map(m => (
                  <div key={m.id} className="p-4 border-b last:border-b-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium capitalize">{m.type.replace(/_/g, ' ')}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{m.reason}</p>
                        {m.reference && <p className="text-xs text-gray-400 dark:text-gray-400">Ref: {m.reference}</p>}
                        <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">{formatDateTime(m.createdAt)}</p>
                      </div>
                      <span className={classNames('text-lg font-bold', m.direction === 'in' ? 'text-green-600 dark:text-green-400' : 'text-red-600')}>
                        {m.direction === 'in' ? '+' : '-'}{formatMoney(m.amount)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-400 mt-2">Saldo: {formatMoney(m.balanceAfter)}</p>
                  </div>
                ))}
              </div>
              <table className="hidden sm:table w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Fecha</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Cuenta</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Tipo</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Concepto</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600 dark:text-gray-300">Monto</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600 dark:text-gray-300">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map(m => (
                    <tr key={m.id} className="border-t">
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{formatDateTime(m.createdAt)}</td>
                      <td className="px-4 py-3 text-sm">{registers.find(r => r.id === m.registerId)?.name || '-'}</td>
                      <td className="px-4 py-3 text-sm capitalize">{m.type.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 text-sm">{m.reason} {m.reference && <span className="text-xs text-gray-400 dark:text-gray-400">({m.reference})</span>}</td>
                      <td className={classNames('px-4 py-3 text-right font-bold', m.direction === 'in' ? 'text-green-600 dark:text-green-400' : 'text-red-600')}>
                        {m.direction === 'in' ? '+' : '-'}{formatMoney(m.amount)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-300">{formatMoney(m.balanceAfter)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ===== TRANSFERS ===== */}
      {tab === 'transfers' && (
        <section>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-lg font-bold">Transferencias</h2>
            <button
              onClick={() => setModal('transfer')}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium min-h-[44px] transition-colors"
            >
              + Nueva Transferencia
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : transfers.length === 0 ? (
            <EmptyState message="No hay transferencias registradas." />
          ) : (
            <div className="space-y-3">
              {transfers.map(t => {
                const fromReg = registers.find(r => r.id === t.fromRegisterId);
                const toReg = registers.find(r => r.id === t.toRegisterId);
                return (
                  <div key={t.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <p className="font-semibold text-gray-800 dark:text-gray-100">{fromReg?.name || 'Origen'} → {toReg?.name || 'Destino'}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{t.reason}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-400">{formatDateTime(t.createdAt)}</p>
                      </div>
                      <span className="text-xl font-bold text-purple-600">{formatMoney(t.amount)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ===== CASHFLOW ===== */}
      {tab === 'cashflow' && (
        <section>
          <h2 className="text-lg font-bold mb-4">Cashflow y Acreditaciones</h2>
          {loading ? (
            <LoadingSpinner />
          ) : cashflow.length === 0 ? (
            <EmptyState message="No hay datos de cashflow." />
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                {cashflow.map(c => {
                  const balance = parseFloat(c.currentBalance || '0');
                  return (
                    <div key={c.registerId} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-gray-800 dark:text-gray-100">{REGISTER_ICONS[c.type]} {c.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{REGISTER_TYPES[c.type] || c.type}</p>
                        </div>
                        <p className={classNames('text-xl font-bold', balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600')}>
                          {formatMoney(balance)}
                        </p>
                      </div>
                      {c.pendingSettlements > 0 && (
                        <div className="mt-3 pt-3 border-t space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Pendientes</span>
                            <span className="font-medium">{c.pendingSettlements}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Neto pendiente</span>
                            <span className="font-medium text-amber-600">{formatMoney(c.pendingNet)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Comisiones</span>
                            <span className="font-medium text-red-600">-{formatMoney(c.pendingCommission)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Disponible ahora</span>
                            <span className="font-bold text-green-600 dark:text-green-400">{formatMoney(c.availableNow)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Proyectado</span>
                            <span className="font-bold text-blue-600 dark:text-blue-400">{formatMoney(c.projectedBalance)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {cashflowTimeline.length > 0 && (
                <>
                  <h3 className="text-base font-bold mb-3">📅 Acreditaciones futuras</h3>
                  <div className="space-y-3">
                    {cashflowTimeline.map(t => {
                      const isDue = new Date(t.date) <= new Date();
                      const net = parseFloat(t.netFlow || '0');
                      return (
                        <div key={t.date} className={classNames('bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4', isDue ? 'border-green-500' : 'border-amber-500')}>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-800 dark:text-gray-100">{formatDate(t.date)}</span>
                              {isDue && <span className="text-xs px-2 py-0.5 bg-green-500 text-white rounded-full font-medium">Vencido</span>}
                            </div>
                            <span className={classNames('font-bold', net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600')}>
                              {net >= 0 ? '+' : ''}{formatMoney(net)}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {(t.inflows || []).map((r, i) => (
                              <p key={`in-${i}`} className="text-sm text-green-600 dark:text-green-400">
                                + {r.registerName || r.type}: {formatMoney(r.net || r.amount)}
                              </p>
                            ))}
                            {(t.outflows || []).map((r, i) => (
                              <p key={`out-${i}`} className="text-sm text-red-600">
                                - {r.poNumber || r.type}: {formatMoney(r.amount)}
                              </p>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </section>
      )}

      {/* ===== CUENTA CORRIENTE ===== */}
      {tab === 'credit' && (
        <section>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-lg font-bold">Estado de cuenta corriente</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Saldo = ventas a crédito − pagos recibidos
            </p>
          </div>
          {loading ? (
            <LoadingSpinner />
          ) : creditSummary.length === 0 ? (
            <EmptyState message="Ningún cliente tiene saldo pendiente en cuenta corriente." />
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left">Cliente</th>
                    <th className="px-4 py-3 text-right">Saldo</th>
                    <th className="px-4 py-3 text-right">Límite</th>
                    <th className="px-4 py-3 text-left">Próx. vencimiento</th>
                    <th className="px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {creditSummary.map(c => (
                    <tr key={c.id} className="border-t">
                      <td className="px-4 py-3 font-medium">{c.name}</td>
                      <td className={`px-4 py-3 text-right font-bold ${c.balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                        ${Number(c.balance).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">${Number(c.creditLimit).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm">
                        {c.nextPaymentDate ? new Date(c.nextPaymentDate).toLocaleDateString('es-AR') : '—'}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => loadHistorico(c)}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium min-h-[44px]"
                          >
                            🕐 Histórico
                          </button>
                          <button
                            onClick={() => { setPayCustomer(c); setPayAmount(''); setPayNote(''); setPayRegisterId(''); }}
                            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium min-h-[44px]"
                          >
                            💳 Registrar pago
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Panel de pago */}
          {payCustomer && (
            <div className="mt-6 border rounded-lg p-5 bg-gray-50 dark:bg-gray-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold">Pago a cuenta corriente — {payCustomer.name}</h3>
                <button onClick={() => setPayCustomer(null)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-2 py-1">✕</button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Saldo pendiente: <strong>${payCustomer.balance.toFixed(2)}</strong>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Monto ($)</label>
                  <input
                    type="number" min="0.01" step="0.01"
                    value={payAmount}
                    onChange={e => setPayAmount(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Caja destino</label>
                  <select
                    value={payRegisterId}
                    onChange={e => setPayRegisterId(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                  >
                    <option value="">(primera caja activa)</option>
                    {registers.filter(r => r.active).map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Nota</label>
                  <input
                    type="text"
                    value={payNote}
                    onChange={e => setPayNote(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                    placeholder="Opcional"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={recordPayment}
                  disabled={paying || !payAmount || Number(payAmount) <= 0}
                  className="px-5 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded-lg font-semibold min-h-[48px] transition-colors"
                >
                  {paying ? 'Registrando...' : 'Confirmar pago'}
                </button>
                <button onClick={() => setPayCustomer(null)} className="px-4 py-2.5 text-gray-600 dark:text-gray-300 min-h-[48px]">Cancelar</button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ===== TERMINALS & SETTLEMENTS ===== */}
      {tab === 'terminals' && (
        <section className="space-y-8">
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-lg font-bold">Terminales de Tarjeta</h2>
              <button
                onClick={() => setModal('terminal')}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium min-h-[44px] transition-colors"
              >
                + Nuevo Terminal
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Configurá los servicios de POS de tarjeta. Al habilitar un terminal, aparece como botón en el POS.
            </p>

            {loading ? (
              <LoadingSpinner />
            ) : cardTerminals.length === 0 ? (
              <EmptyState message="No hay terminales configurados." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cardTerminals.map(t => (
                  <div key={t.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-gray-800 dark:text-gray-100">{t.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 capitalize">{t.provider.replace(/_/g, ' ')}</p>
                      </div>
                      <span className={classNames('text-xs px-2 py-0.5 rounded-full font-medium text-white', t.active ? 'bg-green-500' : 'bg-gray-400')}>
                        {t.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 my-3 text-sm">
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-400">Débito</p>
                        <p className="font-medium">{t.commissionDebit}% · {t.accreditationDaysDebit}d</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-400">Crédito</p>
                        <p className="font-medium">{t.commissionCredit}% · {t.accreditationDaysCredit}d</p>
                      </div>
                    </div>
                    <p className="text-xs text-purple-600 font-medium mb-3">
                      → {registers.find(r => r.id === t.registerId)?.name || 'Sin cuenta'}
                    </p>
                    <button
                      onClick={() => openEditTerminalModal(t)}
                      className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium min-h-[44px] transition-colors"
                    >
                      Editar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-lg font-bold">Acreditaciones de Tarjeta</h2>
              <button
                onClick={accreditSettlements}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium min-h-[44px] transition-colors"
              >
                ✅ Acreditar vencidos
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Cada venta con tarjeta genera una acreditación pendiente. El dinero se acredita automáticamente en la cuenta bancaria a la fecha indicada.
            </p>

            {loading ? (
              <LoadingSpinner />
            ) : settlements.length === 0 ? (
              <EmptyState message="No hay transacciones de tarjeta." />
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="block sm:hidden">
                  {settlements.map(s => {
                    const isDue = s.status === 'pending' && new Date(s.accreditationDate) <= new Date();
                    return (
                      <div key={s.id} className="p-4 border-b last:border-b-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-gray-800 dark:text-gray-100">Venta #{s.saleId?.slice(0, 8)}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{s.cardType === 'credit' ? '💳 Crédito' : '💳 Débito'} · {formatDate(s.saleDate)}</p>
                            <p className={classNames('text-xs mt-1', isDue ? 'text-amber-600 font-medium' : 'text-gray-400 dark:text-gray-400')}>
                              Acreditación: {formatDate(s.accreditationDate)}
                            </p>
                          </div>
                          <span className={classNames('text-xs px-2 py-0.5 rounded-full font-medium text-white', s.status === 'accredited' ? 'bg-green-500' : isDue ? 'bg-amber-500' : 'bg-gray-400')}>
                            {s.status === 'accredited' ? 'Acreditado' : isDue ? 'Vencido' : 'Pendiente'}
                          </span>
                        </div>
                        <div className="mt-2 text-right">
                          <p className="text-sm text-gray-400 dark:text-gray-400">Bruto: {formatMoney(s.grossAmount)}</p>
                          <p className="text-sm text-red-600">Comisión: -{formatMoney(s.commissionAmount)}</p>
                          <p className={classNames('text-lg font-bold', s.status === 'accredited' ? 'text-green-600 dark:text-green-400' : 'text-amber-600')}>
                            Neto: {formatMoney(s.netAmount)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <table className="hidden sm:table w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Venta</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Tarjeta</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Acreditación</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 dark:text-gray-300">Estado</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600 dark:text-gray-300">Neto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settlements.map(s => {
                      const isDue = s.status === 'pending' && new Date(s.accreditationDate) <= new Date();
                      return (
                        <tr key={s.id} className="border-t">
                          <td className="px-4 py-3 text-sm font-medium">#{s.saleId?.slice(0, 8)}</td>
                          <td className="px-4 py-3 text-sm">{s.cardType === 'credit' ? 'Crédito' : 'Débito'}</td>
                          <td className={classNames('px-4 py-3 text-sm', isDue ? 'text-amber-600 font-medium' : 'text-gray-500 dark:text-gray-400 dark:dark:text-gray-400')}>
                            {formatDate(s.accreditationDate)}
                            {s.accreditedAt && <span className="block text-xs text-green-600 dark:text-green-400">✓ {formatDateTime(s.accreditedAt)}</span>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={classNames('text-xs px-2 py-0.5 rounded-full font-medium text-white', s.status === 'accredited' ? 'bg-green-500' : isDue ? 'bg-amber-500' : 'bg-gray-400')}>
                              {s.status === 'accredited' ? 'Acreditado' : isDue ? 'Vencido' : 'Pendiente'}
                            </span>
                          </td>
                          <td className={classNames('px-4 py-3 text-right font-bold', s.status === 'accredited' ? 'text-green-600 dark:text-green-400' : 'text-amber-600')}>
                            {formatMoney(s.netAmount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ===== MODALS ===== */}
      {modal === 'register' && (
        <Modal title="Nueva Cuenta" onClose={() => setModal(null)} onSave={createRegister} saveDisabled={!regForm.name.trim()}>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Nombre (ej: Caja Ejecutiva)"
              value={regForm.name}
              onChange={e => setRegForm({ ...regForm, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            />
            <select
              value={regForm.type}
              onChange={e => setRegForm({ ...regForm, type: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            >
              <option value="cash_drawer">💵 Caja Física</option>
              <option value="safe">🔒 Caja Fuerte</option>
              <option value="bank_account">🏦 Cuenta Bancaria</option>
              <option value="petty_cash">📋 Caja Chica</option>
            </select>
            <input
              type="number"
              step="0.01"
              placeholder="Saldo inicial"
              value={regForm.openingBalance}
              onChange={e => setRegForm({ ...regForm, openingBalance: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            />
            {regForm.type === 'cash_drawer' && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={regForm.isPos}
                  onChange={e => setRegForm({ ...regForm, isPos: e.target.checked })}
                  className="w-4 h-4"
                />
                🛒 Caja del POS (punto de venta)
              </label>
            )}
            {regForm.type === 'bank_account' && (
              <div className="space-y-3 pt-2 border-t">
                <input
                  type="text"
                  placeholder="Banco"
                  value={regForm.bankName}
                  onChange={e => setRegForm({ ...regForm, bankName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
                <input
                  type="text"
                  placeholder="N° de cuenta"
                  value={regForm.accountNumber}
                  onChange={e => setRegForm({ ...regForm, accountNumber: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
                <select
                  value={regForm.accountType}
                  onChange={e => setRegForm({ ...regForm, accountType: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                >
                  <option value="">Tipo de cuenta...</option>
                  <option value="checking">Cuenta Corriente</option>
                  <option value="savings">Caja de Ahorro</option>
                </select>
                <input
                  type="text"
                  placeholder="Alias (ej: mi.comercio.pagos)"
                  value={regForm.alias}
                  onChange={e => setRegForm({ ...regForm, alias: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={regForm.posEnabled}
                    onChange={e => setRegForm({ ...regForm, posEnabled: e.target.checked })}
                    className="w-4 h-4"
                  />
                  Habilitar para recibir transferencias/tarjetas en POS
                </label>
              </div>
            )}
          </div>
        </Modal>
      )}

      {modal === 'editRegister' && editRegister && (
        <Modal title="Editar Cuenta" onClose={() => { setModal(null); setEditRegister(null); }} onSave={saveEditRegister}>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Nombre"
              value={editRegister.name}
              onChange={e => setEditRegister({ ...editRegister, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            />
            {editRegister.type === 'bank_account' && (
              <div className="space-y-3 pt-2 border-t">
                <input
                  type="text"
                  placeholder="Banco"
                  value={editRegister.bankName || ''}
                  onChange={e => setEditRegister({ ...editRegister, bankName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
                <input
                  type="text"
                  placeholder="N° de cuenta"
                  value={editRegister.accountNumber || ''}
                  onChange={e => setEditRegister({ ...editRegister, accountNumber: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
                <select
                  value={editRegister.accountType || ''}
                  onChange={e => setEditRegister({ ...editRegister, accountType: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                >
                  <option value="">Tipo de cuenta...</option>
                  <option value="checking">Cuenta Corriente</option>
                  <option value="savings">Caja de Ahorro</option>
                </select>
                <input
                  type="text"
                  placeholder="Alias"
                  value={editRegister.alias || ''}
                  onChange={e => setEditRegister({ ...editRegister, alias: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editRegister.posEnabled || false}
                    onChange={e => setEditRegister({ ...editRegister, posEnabled: e.target.checked })}
                    className="w-4 h-4"
                  />
                  Habilitar para recibir transferencias/tarjetas en POS
                </label>
              </div>
            )}
          </div>
        </Modal>
      )}

      {modal === 'openShift' && (
        <Modal title="Abrir Caja" onClose={() => setModal(null)} onSave={openShift} saveDisabled={!shiftForm.registerId || !shiftForm.openingBalance}>
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">Solo las cajas físicas se abren y cierran con arqueo. Las cuentas bancarias siempre están abiertas.</p>
            <select
              value={shiftForm.registerId}
              onChange={e => setShiftForm({ ...shiftForm, registerId: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            >
              <option value="">Seleccionar caja...</option>
              {registers.filter(r => r.type === 'cash_drawer').map(r => {
                const openShift = openShifts.find(s => s.registerId === r.id);
                return (
                  <option key={r.id} value={r.id} disabled={!!openShift}>
                    {openShift
                      ? `⛔ Ya abierta por ${openShift.openedByName || 'otro usuario'} · `
                      : ''}
                    {r.name} — Efectivo disponible: {formatMoney(r.currentBalance)}
                  </option>
                );
              })}
            </select>
            <input
              type="number"
              step="0.01"
              placeholder="Fondo/arqueo de apertura ($)"
              value={shiftForm.openingBalance}
              onChange={e => setShiftForm({ ...shiftForm, openingBalance: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">Contá el efectivo real de la caja y cargalo acá.</p>
            <input
              type="text"
              placeholder="Nota (opcional)"
              value={shiftForm.note}
              onChange={e => setShiftForm({ ...shiftForm, note: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            />
          </div>
        </Modal>
      )}

      {modal === 'closeShift' && (
        <Modal title="Cerrar Caja" onClose={() => setModal(null)} onSave={closeShift} saveDisabled={!closeForm.countedBalance}>
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">Contá el efectivo en la caja e ingresá el monto real.</p>
            <input
              type="number"
              step="0.01"
              placeholder="Monto contado ($)"
              value={closeForm.countedBalance}
              onChange={e => setCloseForm({ ...closeForm, countedBalance: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            />
            <input
              type="text"
              placeholder="Nota de cierre (opcional)"
              value={closeForm.note}
              onChange={e => setCloseForm({ ...closeForm, note: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            />
          </div>
        </Modal>
      )}

      {/* Aporte/retiro de efectivo (owner/manager) */}
      {cashOpForm && (
        <Modal
          title={cashOpForm.direction === 'in' ? '💵 Aportar efectivo a la caja' : '🏦 Retirar efectivo de la caja'}
          onClose={() => setCashOpForm(null)}
          onSave={submitCashOp}
          saveDisabled={!cashOpForm.amount || !cashOpForm.reason.trim()}
        >
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {cashOpForm.direction === 'in'
                ? 'Registrá el efectivo que se agrega a la caja (ej: cambio). Queda como movimiento de caja.'
                : 'Registrá el efectivo que se retira de la caja. Queda como movimiento de caja.'}
            </p>
            <input
              type="number"
              step="0.01"
              placeholder="Monto ($)"
              value={cashOpForm.amount}
              onChange={e => setCashOpForm({ ...cashOpForm, amount: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
              autoFocus
            />
            <input
              type="text"
              placeholder="Motivo *"
              value={cashOpForm.reason}
              onChange={e => setCashOpForm({ ...cashOpForm, reason: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            />
          </div>
        </Modal>
      )}

      {modal === 'transfer' && (
        <Modal title="Transferir entre Cuentas" onClose={() => setModal(null)} onSave={createTransfer} saveDisabled={!transferForm.fromRegisterId || !transferForm.toRegisterId || !transferForm.amount || !transferForm.reason}>
          <div className="space-y-3">
            <select
              value={transferForm.fromRegisterId}
              onChange={e => setTransferForm({ ...transferForm, fromRegisterId: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            >
              <option value="">Desde (origen)...</option>
              {registers.map(r => (
                <option key={r.id} value={r.id}>{r.name} ({formatMoney(r.currentBalance)})</option>
              ))}
            </select>
            <select
              value={transferForm.toRegisterId}
              onChange={e => setTransferForm({ ...transferForm, toRegisterId: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            >
              <option value="">Hacia (destino)...</option>
              {registers.filter(r => r.id !== transferForm.fromRegisterId).map(r => (
                <option key={r.id} value={r.id}>{r.name} ({formatMoney(r.currentBalance)})</option>
              ))}
            </select>
            <input
              type="number"
              step="0.01"
              placeholder="Monto ($)"
              value={transferForm.amount}
              onChange={e => setTransferForm({ ...transferForm, amount: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            />
            <input
              type="text"
              placeholder="Motivo (obligatorio)"
              value={transferForm.reason}
              onChange={e => setTransferForm({ ...transferForm, reason: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            />
          </div>
        </Modal>
      )}

      {modal === 'terminal' && (
        <Modal title="Nuevo Terminal de Tarjeta" onClose={() => setModal(null)} onSave={createTerminal} saveDisabled={!termForm.name || !termForm.provider || !termForm.registerId}>
          <div className="space-y-3">
            <select
              value={termForm.provider}
              onChange={e => applyProviderPreset(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            >
              <option value="mercado_pago">Mercado Pago Point</option>
              <option value="uala">Ualá POS</option>
              <option value="modo">MODO (Bancos)</option>
              <option value="bank">Posnet Bancario</option>
              <option value="first_data">LaPos / Getnet</option>
              <option value="other">Otro</option>
            </select>
            <input
              type="text"
              placeholder="Nombre (ej: Posnet Local)"
              value={termForm.name}
              onChange={e => setTermForm({ ...termForm, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Comisión Débito (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={termForm.commissionDebit}
                  onChange={e => setTermForm({ ...termForm, commissionDebit: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Comisión Crédito (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={termForm.commissionCredit}
                  onChange={e => setTermForm({ ...termForm, commissionCredit: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Acreditación Débito (días)</label>
                <input
                  type="number"
                  value={termForm.accreditationDaysDebit}
                  onChange={e => setTermForm({ ...termForm, accreditationDaysDebit: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Acreditación Crédito (días)</label>
                <input
                  type="number"
                  value={termForm.accreditationDaysCredit}
                  onChange={e => setTermForm({ ...termForm, accreditationDaysCredit: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Cuenta bancaria para acreditación</label>
              <select
                value={termForm.registerId}
                onChange={e => setTermForm({ ...termForm, registerId: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
              >
                <option value="">Seleccionar...</option>
                {registers.filter(r => r.type === 'bank_account').map(r => (
                  <option key={r.id} value={r.id}>{r.name} {r.bankName ? `(${r.bankName})` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Tarjetas aceptadas</label>
              <select
                value={termForm.acceptedCards}
                onChange={e => setTermForm({ ...termForm, acceptedCards: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]">
                <option value="both">Débito y Crédito</option>
                <option value="debit">Solo Débito</option>
                <option value="credit">Solo Crédito</option>
              </select>
            </div>
          </div>
        </Modal>
      )}

      {modal === 'editTerminal' && editTerminal && (
        <Modal title="Editar Terminal" onClose={() => { setModal(null); setEditTerminal(null); }} onSave={saveEditTerminal}>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Nombre"
              value={editTerminal.name}
              onChange={e => setEditTerminal({ ...editTerminal, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            />
            <select
              value={editTerminal.provider}
              onChange={e => setEditTerminal({ ...editTerminal, provider: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
            >
              <option value="mercado_pago">Mercado Pago Point</option>
              <option value="uala">Ualá POS</option>
              <option value="modo">MODO (Bancos)</option>
              <option value="bank">Posnet Bancario</option>
              <option value="first_data">LaPos / Getnet</option>
              <option value="other">Otro</option>
            </select>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Comisión Débito (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editTerminal.commissionDebit}
                  onChange={e => setEditTerminal({ ...editTerminal, commissionDebit: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Comisión Crédito (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editTerminal.commissionCredit}
                  onChange={e => setEditTerminal({ ...editTerminal, commissionCredit: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Acreditación Débito (días)</label>
                <input
                  type="number"
                  value={editTerminal.accreditationDaysDebit}
                  onChange={e => setEditTerminal({ ...editTerminal, accreditationDaysDebit: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Acreditación Crédito (días)</label>
                <input
                  type="number"
                  value={editTerminal.accreditationDaysCredit}
                  onChange={e => setEditTerminal({ ...editTerminal, accreditationDaysCredit: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Cuenta bancaria para acreditación</label>
              <select
                value={editTerminal.registerId}
                onChange={e => setEditTerminal({ ...editTerminal, registerId: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
              >
                <option value="">Seleccionar...</option>
                {registers.filter(r => r.type === 'bank_account').map(r => (
                  <option key={r.id} value={r.id}>{r.name} {r.bankName ? `(${r.bankName})` : ''}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={editTerminal.active}
                onChange={e => setEditTerminal({ ...editTerminal, active: e.target.checked })}
                className="w-4 h-4"
              />
              Terminal activo (aparece en POS)
            </label>
          </div>
        </Modal>
      )}

      {/* Modal Histórico de cuenta corriente */}
      {histCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setHistCustomer(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="font-bold text-lg">Histórico — {histCustomer.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Límite: ${Number(histCustomer.creditLimit).toFixed(2)} · Plazo: {histCustomer.creditDays} días ·
                  Saldo actual: {' '}
                  <span className={`font-bold ${histCustomer.balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    ${Number(histCustomer.balance).toFixed(2)}
                  </span>
                </p>
              </div>
              <button onClick={() => setHistCustomer(null)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-2 py-1 text-xl leading-none">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {histLoading ? (
                <p className="text-sm text-gray-500">Cargando movimientos...</p>
              ) : histMovements.length === 0 ? (
                <p className="text-sm text-gray-500">Sin movimientos registrados.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left">Fecha</th>
                      <th className="px-3 py-2 text-left">Tipo</th>
                      <th className="px-3 py-2 text-right">Monto</th>
                      <th className="px-3 py-2 text-left">Venc. / pago</th>
                      <th className="px-3 py-2 text-left">Estado</th>
                      <th className="px-3 py-2 text-left">Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {histMovements.map(m => (
                      <tr key={m.id} className="border-t">
                        <td className="px-3 py-2">{new Date(m.createdAt).toLocaleDateString('es-AR')}</td>
                        <td className="px-3 py-2">
                          {m.type === 'debit' ? (
                            <span className="text-red-600 dark:text-red-400">Débito (venta)</span>
                          ) : (
                            <span className="text-green-600 dark:text-green-400">Crédito (pago)</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold">${Number(m.amount).toFixed(2)}</td>
                        <td className="px-3 py-2">{m.paymentDate ? new Date(m.paymentDate).toLocaleDateString('es-AR') : '—'}</td>
                        <td className="px-3 py-2">
                          {m.status === 'paid'
                            ? <span className="px-2 py-0.5 rounded text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">Pagado</span>
                            : <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300">Pendiente</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{m.note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
